import { defineStore } from 'pinia'
import { usePlaceStore, useAlertStore, useUnitStore } from './'
import { updateStoreState, deleteStoreState } from '../helpers/stateUtils'
import Vue from 'vue'
import { BEAM_API } from '../constants'
import { normalize, schema } from 'normalizr'

const extraSchema = new schema.Entity('extras')

export const useExtraStore = defineStore({
  id: 'extra',
  state: () => ({
    extras: [],
  }),
  getters: {
    findById: (state) => (id) => state.extras.find((find) => find.id === id),
    findByIds: (state) => (ids) => state.extras.filter((find) => ids.includes(find.id)),
  },
  actions: {
    httpGetExtrasByPlace ({ place }) {
      const placeStore = usePlaceStore()
      const alertStore = useAlertStore()
      const unitStore = useUnitStore()

      return Vue.prototype.$httpEZ
        .get(`${BEAM_API}/extras?place=${place.id}`)
        .then((response) => {
          const { entities } = normalize(response.data['hydra:member'], [extraSchema])
          if (entities.extras) {
            updateStoreState(this.extras, Object.values(entities.extras))

            Object.values(entities.extras).forEach(extra => {
              // update extra relation on all units
              unitStore.units.forEach((unit) => {
                // remove the extra from any existing unit
                const i = unit.extras.findIndex((extra) => extra === extra.id)
                if (i > -1) {
                  unit.extras.splice(i, 1)
                }

                // add extra onto any matching units
                extra.units.forEach((extraUnit) => {
                  if (unit.id === extraUnit.id) {
                    unit.extras.push(extra.id)
                  }
                })

                updateStoreState(unitStore.units, unit)
              })
            })

            // update the place with extra keys
            place.extras = Object.keys(entities.extras)
            updateStoreState(placeStore.places, place)
          }
        })
        .catch(() => {
          alertStore.setAlertError({ message: '*There was a problem fetching the extras*' })
        })
    },
    httpCreateExtra ({ extra, place }) {
      const placeStore = usePlaceStore()
      const alertStore = useAlertStore()
      const unitStore = useUnitStore()

      return Vue.prototype.$httpEZ
        .post(`${BEAM_API}/extras`, extra)
        .then((response) => {
          updateStoreState(this.extras, response.data)

          const extraId = response.data.id

          // update extra relation on all units
          unitStore.units.forEach((unit) => {
            // remove the extra from any existing unit
            const i = unit.extras.findIndex((extra) => extra === extraId)
            if (i > -1) {
              unit.extras.splice(i, 1)
            }

            // add extra onto any matching units
            response.data.units.forEach((extraUnit) => {
              if (unit.id === extraUnit.id) {
                unit.extras.push(extraId)
              }
            })

            updateStoreState(unitStore.units, unit)
          })

          place.extras.push(response.data.id)
          updateStoreState(placeStore.places, place)

          alertStore.setAlertSuccess({ message: '*Extra added*' })
        })
        .catch(() => {
          alertStore.setAlertError({ message: '*Adding extra failed*' })
        })
    },
    httpUpdateExtra ({ extra }) {
      const alertStore = useAlertStore()
      const unitStore = useUnitStore()

      return Vue.prototype.$httpEZ
        .put(`${BEAM_API}/extras/${extra.id}`, extra)
        .then((response) => {
          const extraId = response.data.id

          // update extra relation on all units
          unitStore.units.forEach((unit) => {
            // remove the extra from any existing unit
            const i = unit.extras.findIndex((extra) => extra === extraId)
            if (i > -1) {
              unit.extras.splice(i, 1)
            }

            // add extra onto any matching units
            response.data.units.forEach((extraUnit) => {
              if (unit.id === extraUnit.id) {
                unit.extras.push(extraId)
              }
            })

            updateStoreState(unitStore.units, unit)
          })

          updateStoreState(this.extras, response.data)
          alertStore.setAlertSuccess({ message: '*Extra has been successfully updated*' })
        })
        .catch((err) => {
          alertStore.setAlertError({ message: '*There was a problem updating this extra*' })

          throw err
        })
    },
    httpDeleteExtra ({ extra, place }) {
      const placeStore = usePlaceStore()
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ
        .delete(`${BEAM_API}/extras/${extra.id}`)
        .then(() => {
          deleteStoreState(this.extras, extra.id)

          // remove the relation from the place
          place.extras = place.extras.filter((item) => item !== extra.id)
          updateStoreState(placeStore.places, place)

          alertStore.setAlertSuccess({ message: '*Extra has been deleted*' })
        })
        .catch(() => {
          alertStore.setAlertError({ message: '*There was a problem deleting this extra*' })
        })
    },
  },
})
